import { useTranslation } from "react-i18next";
import { deriveStatus, overtimeFee, progressPercent } from "@astroplanet/shared";
import { useLocale } from "../../hooks/useLocale";
import { useNow } from "../../hooks/useNow";
import { formatCountdown, money } from "../../lib/format";
import type { SessionRules, SessionView } from "../../lib/types";

/** Everything time-related for one session, computed locally from the shared rules so it ticks every second. */
export function useLiveSession(s: SessionView, rules: SessionRules) {
  const nowMs = useNow();
  const now = new Date(nowMs);
  const end = new Date(s.endAt);
  return {
    seconds: Math.round((end.getTime() - nowMs) / 1000),
    status: deriveStatus(end, now, rules.warnMinutes),
    fee: overtimeFee(end, now, rules.graceMinutes, rules.overtimeFilsPer15m),
    percent: progressPercent(new Date(s.startAt), end, now),
  };
}

/** Post-paid: how long the current block lasts and what the visit costs so far (the bill follows the clock, never "overdue"). */
function PostpaidRemaining({ session }: { session: SessionView }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const nowMs = useNow();
  const p = session.postpaid!;
  const seconds = Math.max(0, Math.round((new Date(session.endAt).getTime() - nowMs) / 1000));
  const soon = seconds <= p.notifyBeforeMinutes * 60;
  return (
    <div data-testid="postpaid-remaining">
      <div className={`flex items-center gap-1.5 font-black ${soon ? "text-solar-orange" : "text-emerald-600"}`}>
        <span aria-hidden>{soon ? "⏳" : "⏱️"}</span>
        <span className="num">{formatCountdown(seconds, locale)}</span>
        <span className="text-[10px] font-bold">{t("postpaid.blockLeft", { n: p.block.index })}</span>
      </div>
      <div className="text-[10px] font-bold text-gray-500">
        🧾 {t("postpaid.runningBill")}: <span className="num font-mono text-cosmic-purple">{money(p.runningBill)}</span> {t("currency")}
      </div>
    </div>
  );
}

export function TimeRemaining({ session, rules }: { session: SessionView; rules: SessionRules }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const live = useLiveSession(session, rules);
  if (session.billingMode === "postpaid" && session.postpaid) return <PostpaidRemaining session={session} />;
  const over = live.seconds < 0;
  const color = live.status === "overdue" ? "text-red-600" : live.status === "expiring" ? "text-solar-orange" : "text-emerald-600";
  const bar = live.status === "overdue" ? "bg-red-500" : live.status === "expiring" ? "bg-solar-orange" : "bg-emerald-500";
  return (
    <div>
      <div className={`flex items-center gap-1.5 font-black ${color}`}>
        <span aria-hidden>{over ? "⚠️" : live.status === "expiring" ? "⏳" : "⏱️"}</span>
        <span className="num">{over ? "+" : ""}{formatCountdown(live.seconds, locale)}</span>
        <span className="text-[10px] font-bold">{over ? t("table.overdueSuffix") : t("table.left")}</span>
      </div>
      {over ? (
        live.fee > 0 && <div className="text-[10px] font-bold text-red-500">{t("table.fee")}: <span className="num">+{money(live.fee)}</span> {t("currency")}</div>
      ) : (
        <div className="mt-1 h-1.5 w-20 overflow-hidden rounded-full bg-gray-200">
          <div className={`h-full rounded-full ${bar}`} style={{ width: `${live.percent}%` }} />
        </div>
      )}
    </div>
  );
}
